import React, { useEffect, useMemo, useRef } from 'react'
import PropTypes from 'prop-types'
import classnames from 'classnames'
import { Controller, useWatch } from 'react-hook-form'

import Form from '../../../../components/forms/layout-form'
import Row from '../../../../components/forms/layout-row'
import Col from '../../../../components/forms/layout-column'
import Input from '../../../../components/forms/input'
import Toggle from '../../../../components/forms/toggle'
import AutoplayToggle from '../../../../components/shared/settings/toggle'
import Select from '../../../../components/forms/select'
import Button from '../../../../components/button'
import ProfileAvatar from '../profile-avatar'
import ProfilesAvatarForm from '../profiles-avatar-form'
import getGenderOptions from '../../../../utils/getGenderOptions'
import { REGEX } from '../../../../utils/regex'
import { getKidsParentalRatingOptions } from '../../../../lib/parental-ratings'
import {
  FORM_MESSAGES,
  FORM_VALUES,
  PHONE_MAX_LENGTH,
  PHONE_REG_EXP
} from '../../../../constants'
import styles from './profiles-form.css'

const validateProfileName = value => {
  const name = value?.trim() || ''

  if (!name) {
    return FORM_MESSAGES.required
  }

  return name.length <= 12 || 'name must be at most 12 characters'
}

const mobileValidationRules = {
  minLength: value => {
    const mobile = value?.trim() || ''

    return !mobile ||
      mobile.length >= FORM_VALUES.mobile.min ||
      FORM_MESSAGES.mobile.min
  },
  pattern: value => {
    const mobile = value?.trim() || ''

    return !mobile || PHONE_REG_EXP.test(mobile) || FORM_MESSAGES.mobile.match
  },
  maxLength: value => {
    const mobile = value?.trim() || ''

    return !mobile ||
      mobile.length <= FORM_VALUES.mobile.max ||
      FORM_MESSAGES.mobile.max
  }
}

function ProfilesForm({
  ageGroupOptions,
  avatars,
  birthYearOptions,
  control,
  error,
  errors,
  handleSubmit,
  header,
  isAvatarSelected,
  isSubmitted,
  isSubmitting,
  onCancel,
  parentalRatingOptions,
  parentalRatingsData,
  setIsAvatarSelected,
  setValue,
  touchedFields
}) {
  const values = useWatch({ control })
  const { ageGroup, isKid: isKidValue } = values
  const isKid = Boolean(isKidValue)
  const previousIsKid = useRef(isKid)
  const previousAgeGroupId = useRef(ageGroup?.value?.id)
  const genderOptions = getGenderOptions()
  const availableParentalRatingOptions = useMemo(() => {
    if (!isKid) {
      return parentalRatingOptions
    }

    return getKidsParentalRatingOptions(
      parentalRatingsData,
      ageGroup?.value?.maxRating
    )
  }, [ageGroup, isKid, parentalRatingOptions, parentalRatingsData])

  useEffect(() => {
    if (previousIsKid.current === isKid) {
      return
    }

    previousIsKid.current = isKid
    const nextAgeGroup = isKid
      ? ageGroupOptions[ageGroupOptions.length - 1]
      : undefined
    const nextParentalRatingOptions = isKid
      ? getKidsParentalRatingOptions(
        parentalRatingsData,
        nextAgeGroup?.value?.maxRating
      )
      : parentalRatingOptions

    setValue('ageGroup', nextAgeGroup, {
      shouldDirty: true,
      shouldValidate: true
    })
    setValue(
      'parentalRating',
      nextParentalRatingOptions[nextParentalRatingOptions.length - 1],
      {
        shouldDirty: true,
        shouldValidate: true
      }
    )
  }, [ageGroupOptions, isKid, parentalRatingOptions, parentalRatingsData, setValue])

  useEffect(() => {
    const ageGroupId = ageGroup?.value?.id

    if (previousAgeGroupId.current === ageGroupId) {
      return
    }

    previousAgeGroupId.current = ageGroupId
    const nextParentalRatingOptions = isKid
      ? getKidsParentalRatingOptions(
        parentalRatingsData,
        ageGroup?.value?.maxRating
      )
      : parentalRatingOptions

    setValue(
      'parentalRating',
      nextParentalRatingOptions[nextParentalRatingOptions.length - 1],
      {
        shouldDirty: true,
        shouldValidate: true
      }
    )
  }, [ageGroup, isKid, parentalRatingOptions, parentalRatingsData, setValue])

  const getWarning = (fieldName, value) => {
    return (value || touchedFields[fieldName] || isSubmitted) &&
      errors[fieldName]?.message
  }

  const birthYearWarning = getWarning('birthYear', values.birthYear)
  const genderWarning = getWarning('gender', values.gender)
  const nonKidsInput = (
    <div>
      <Controller
        name="email"
        control={control}
        rules={{
          pattern: {
            value: REGEX.email,
            message: FORM_MESSAGES.email.valid
          }
        }}
        render={({ field }) => (
          <Input
            className={styles.full}
            label="Email"
            name={field.name}
            value={field.value}
            onChange={field.onChange}
            onBlur={field.onBlur}
            setRef={field.ref}
            warning={getWarning(field.name, field.value)}
            isValid={Boolean(field.value && !errors[field.name])}
          />
        )}
      />

      <div>
        <div className={styles.inputWrapper}>
          <Controller
            name="birthYear"
            control={control}
            rules={{ required: FORM_MESSAGES.required }}
            render={({ field }) => (
              <Select
                wrapperClass={styles.half}
                name={field.name}
                label="Year of birth"
                value={field.value}
                options={birthYearOptions}
                onChange={field.onChange}
                onBlur={field.onBlur}
                setRef={field.ref}
                warning={birthYearWarning}
                isValid={Boolean(
                  field.value &&
                  touchedFields[field.name] &&
                  !errors[field.name]
                )}
                exclamationClass={styles.noExclamation}
                isRequired
                plainTheme
              />
            )}
          />
          <Controller
            name="gender"
            control={control}
            rules={{ required: FORM_MESSAGES.required }}
            render={({ field }) => (
              <Select
                wrapperClass={styles.half}
                name={field.name}
                label="Gender"
                value={field.value}
                options={genderOptions}
                onChange={field.onChange}
                onBlur={field.onBlur}
                setRef={field.ref}
                warning={genderWarning}
                isValid={Boolean(
                  field.value &&
                  touchedFields[field.name] &&
                  !errors[field.name]
                )}
                exclamationClass={styles.noExclamation}
                isRequired
                plainTheme
              />
            )}
          />
        </div>
        <div
          className={classnames(
            styles.ageAndGenderPolicy,
            (birthYearWarning || genderWarning) && styles.errorExtraMargin
          )}
        >
          <span>
            We’ll use this information to create a more personalised experience
            on NEON for you.
          </span>
        </div>
      </div>

      <Controller
        name="mobile"
        control={control}
        rules={{ validate: mobileValidationRules }}
        render={({ field }) => (
          <Input
            className={styles.half}
            label="Mobile"
            name={field.name}
            value={field.value}
            onChange={field.onChange}
            onBlur={field.onBlur}
            setRef={field.ref}
            warning={getWarning(field.name, field.value)}
            isValid={Boolean(field.value && !errors[field.name])}
            inputmode="numeric"
            maxLength={PHONE_MAX_LENGTH}
          />
        )}
      />

      <div className={classnames(styles.mobileExtraInfo, errors.mobile && styles.errorExtraMargin)}>
        <p>
          (Why? So we can tell you about new shows you&#39;ll like)
        </p>
      </div>
    </div>
  )
  const kidsToggle = (
    <div className={styles.kidsToggleWrapper}>
      <Controller
        name="isKid"
        control={control}
        render={({ field }) => (
          <Toggle
            id="kidsProfile"
            label="Kids profile"
            name={field.name}
            checked={field.value}
            border={!field.value}
            onChange={event => field.onChange(event.target.checked)}
            className={classnames(
              styles.half,
              styles.toggleBorderFix
            )}
          />
        )}
      />
      {
        isKid
          ? (
            <Controller
              name="ageGroup"
              control={control}
              render={({ field }) => (
                <Select
                  wrapperClass={classnames(
                    styles.selectWrapper,
                    styles.half
                  )}
                  name={field.name}
                  value={field.value}
                  options={ageGroupOptions}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  setRef={field.ref}
                  plainTheme
                />
              )}
            />
          )
          : null
      }
    </div>
  )

  return (
    <div
      className={classnames(styles.wrapper, {
        [styles.kidsProfile]: isKid
      })}
    >
      <Form
        isSubmitting={isSubmitting}
        handleSubmit={handleSubmit}
      >
        {
          isAvatarSelected || !values.avatar
            ? (
              <Controller
                name="avatar"
                control={control}
                render={({ field }) => (
                  <ProfilesAvatarForm
                    setIsAvatarSelected={setIsAvatarSelected}
                    selectedAvatar={field.value}
                    setSelectedAvatar={field.onChange}
                    avatars={avatars}
                    onCancel={onCancel}
                  />
                )}
              />
            )
            : (
              <div>
                <div className={styles.header}>
                  <h1 className={styles.title}>{header}</h1>
                  <span className={styles.subHeader}>
                    You can set up your profile here.
                  </span>
                </div>

                <div className={styles.formRowWrap}>
                  <div className={styles.avatarColumn}>
                    <div>
                      <ProfileAvatar uri={values.avatar.uri} />
                    </div>
                    <Button
                      className={styles.selectAvatarButton}
                      onClick={() => {
                        setIsAvatarSelected(true)
                      }}
                      text="Edit Pic"
                      secondary
                      disabled={false}
                    />
                  </div>
                  <div className={styles.inputColumn}>
                    {
                      values.isDefault
                        ? <div>Default profile</div>
                        : kidsToggle
                    }
                    <div className={styles.inputWrapper}>
                      <Controller
                        name="name"
                        control={control}
                        rules={{ validate: validateProfileName }}
                        render={({ field }) => (
                          <Input
                            className={styles.half}
                            label="Name"
                            name={field.name}
                            value={field.value}
                            onChange={field.onChange}
                            onBlur={field.onBlur}
                            setRef={field.ref}
                            warning={getWarning(field.name, field.value)}
                            isValid={Boolean(field.value && !errors[field.name])}
                            isRequired
                          />
                        )}
                      />
                      <Controller
                        name="parentalRating"
                        control={control}
                        rules={{ required: FORM_MESSAGES.required }}
                        render={({ field }) => (
                          <Select
                            wrapperClass={styles.half}
                            name={field.name}
                            label="Content rating"
                            value={field.value}
                            options={availableParentalRatingOptions}
                            onChange={field.onChange}
                            onBlur={field.onBlur}
                            setRef={field.ref}
                            warning={getWarning(field.name, field.value)}
                            isValid={Boolean(
                              field.value &&
                              touchedFields[field.name] &&
                              !errors[field.name]
                            )}
                            isRequired
                            plainTheme
                          />
                        )}
                      />
                    </div>
                    {
                      isKid ? null : nonKidsInput
                    }

                    <div>
                      <div className={styles.toggleTitle}>Playback settings</div>

                      <Controller
                        name="nextEpisodeAutoplay"
                        control={control}
                        render={({ field }) => (
                          <AutoplayToggle
                            data-e2e="autoplay-next-episode-toggle"
                            wrapperClassName={styles.toggleWrapperClass}
                            textWrapperClassName={styles.textWrapperClass}
                            containerClassName={styles.toggleTextClass}
                            text="Autoplay next episode"
                            subText="Automatically play the next episode when the credits begin."
                            toggleText={field.value ? 'On' : 'Off'}
                            checked={field.value || false}
                            onChange={event => field.onChange(event.target.checked)}
                          />
                        )}
                      />

                      <div className={styles.toggleDescription}>
                        Playback settings apply across all devices for this profile.
                      </div>
                    </div>

                    <div>
                      <div className={styles.toggleTitle}>Trailer settings</div>

                      <Controller
                        name="trailerAutoplayOnBrowsePage"
                        control={control}
                        render={({ field }) => (
                          <AutoplayToggle
                            data-e2e="autoplay-trailer-home-page-toggle"
                            wrapperClassName={styles.toggleWrapperClass}
                            textWrapperClassName={styles.textWrapperClass}
                            containerClassName={styles.toggleTextClass}
                            text="Autoplay on homepages"
                            subText="Automatically play trailers when browsing the homepages."
                            toggleText={field.value ? 'On' : 'Off'}
                            checked={field.value || false}
                            onChange={event => field.onChange(event.target.checked)}
                          />
                        )}
                      />

                      <br />

                      <Controller
                        name="trailerAutoplayOnShowPage"
                        control={control}
                        render={({ field }) => (
                          <AutoplayToggle
                            data-e2e="autoplay-trailer-show-page-toggle"
                            wrapperClassName={styles.toggleWrapperClass}
                            textWrapperClassName={styles.textWrapperClass}
                            containerClassName={styles.toggleTextClass}
                            text="Autoplay on detail pages"
                            subText="Automatically play trailers when browsing the content detail pages."
                            toggleText={field.value ? 'On' : 'Off'}
                            checked={field.value || false}
                            onChange={event => field.onChange(event.target.checked)}
                          />
                        )}
                      />

                      <div className={styles.toggleDescription}>
                        Trailer settings apply across all devices for this profile.
                      </div>
                    </div>
                  </div>
                </div>

                <Row>
                  <div className={styles.termsAndPrivacy}>
                    { !isKid && (
                      <p>
                        (By providing your details, you agree to the Neon
                        Terms of Service and Privacy Policy)
                      </p>
                    )}
                  </div>
                </Row>
                <Row className={styles.buttonWrapper}>
                  <Col className={styles.saveWrapper}>
                    <Button
                      disabled={isSubmitting}
                      text="Save"
                      error={error}
                      lightBGPrimary
                    />
                  </Col>
                  <Col className={styles.cancelWrapper}>
                    <Button
                      disabled={isSubmitting}
                      text="Cancel"
                      onClick={(event) => { event.preventDefault(); onCancel() }}
                      secondary
                    />
                  </Col>
                </Row>

                <div className={styles.footer}>
                  <p>
                    Looking for more options?
                    <a href="/my-account" target="_blank" rel="noopener noreferrer">Account Settings</a>
                  </p>
                </div>
              </div>
            )
        }
      </Form>
    </div>
  )
}

ProfilesForm.propTypes = {
  ageGroupOptions: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.object])).isRequired,
  avatars: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.object])).isRequired,
  birthYearOptions: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.object])).isRequired,
  control: PropTypes.oneOfType([PropTypes.object]).isRequired,
  error: PropTypes.string,
  errors: PropTypes.oneOfType([PropTypes.object]).isRequired,
  handleSubmit: PropTypes.func.isRequired,
  header: PropTypes.string,
  isAvatarSelected: PropTypes.bool.isRequired,
  isSubmitted: PropTypes.bool.isRequired,
  isSubmitting: PropTypes.bool.isRequired,
  onCancel: PropTypes.func.isRequired,
  parentalRatingOptions: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.object])).isRequired,
  parentalRatingsData: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.object])).isRequired,
  setIsAvatarSelected: PropTypes.func.isRequired,
  setValue: PropTypes.func.isRequired,
  touchedFields: PropTypes.oneOfType([PropTypes.object]).isRequired
}

ProfilesForm.defaultProps = {
  error: '',
  header: ''
}

export default ProfilesForm
